class TootoColorCollection extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cards = Array.from(this.querySelectorAll('[data-tooto-color-card]'));
    this.track = this.querySelector('[data-tooto-color-track]');
    this.range = this.querySelector('[data-tooto-color-range]');
    this.sliderThumb = this.querySelector('[data-tooto-color-slider-thumb]');
    this.output = this.querySelector('[data-tooto-color-output]');
    this.viewButtons = Array.from(this.querySelectorAll('[data-tooto-color-view]'));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.activeIndex = this.clampIndex(Number(this.dataset.initialIndex) || 0);
    this.activeView = 'front';
    this.swipe = null;
    this.ignoreClickUntil = 0;

    this.handleRangeInput = (event) => this.selectCard(Number(event.currentTarget.value));
    this.handleCardClick = (event) => {
      if (performance.now() < this.ignoreClickUntil) {
        event.preventDefault();
        return;
      }

      this.selectCard(Number(event.currentTarget.dataset.cardIndex));
    };
    this.handleCardKeydown = (event) => this.onCardKeydown(event);
    this.handleViewClick = (event) => this.selectView(event.currentTarget.dataset.tootoColorView);
    this.handlePointerDown = (event) => this.onPointerDown(event);
    this.handlePointerMove = (event) => this.onPointerMove(event);
    this.handlePointerUp = (event) => this.onPointerUp(event);
    this.handlePointerCancel = () => this.cancelSwipe();
    this.handleResize = () => this.updateSliderThumb(false);

    this.range?.addEventListener('input', this.handleRangeInput);
    this.track?.addEventListener('pointerdown', this.handlePointerDown);
    this.track?.addEventListener('pointermove', this.handlePointerMove);
    this.track?.addEventListener('pointerup', this.handlePointerUp);
    this.track?.addEventListener('pointercancel', this.handlePointerCancel);
    this.cards.forEach((card) => {
      card.addEventListener('click', this.handleCardClick);
      card.addEventListener('keydown', this.handleCardKeydown);
    });
    this.viewButtons.forEach((button) => button.addEventListener('click', this.handleViewClick));
    window.addEventListener('resize', this.handleResize, { passive: true });

    if (this.range && this.sliderThumb) this.classList.add('is-slider-enhanced');
    this.render(false);
    requestAnimationFrame(() => this.classList.add('is-slider-ready'));
    this.preloadNearbySideImages();
  }

  disconnectedCallback() {
    this.range?.removeEventListener('input', this.handleRangeInput);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointermove', this.handlePointerMove);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.handlePointerCancel);
    this.cards?.forEach((card) => {
      card.removeEventListener('click', this.handleCardClick);
      card.removeEventListener('keydown', this.handleCardKeydown);
      card.getAnimations().forEach((animation) => animation.cancel());
    });
    this.viewButtons?.forEach((button) => button.removeEventListener('click', this.handleViewClick));
    window.removeEventListener('resize', this.handleResize);
  }

  onPointerDown(event) {
    if (window.innerWidth >= 768) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.swipe = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
      axis: null,
    };
  }

  onPointerMove(event) {
    if (!this.swipe || event.pointerId !== this.swipe.pointerId) return;

    this.swipe.currentX = event.clientX;
    this.swipe.currentY = event.clientY;

    const deltaX = this.swipe.currentX - this.swipe.startX;
    const deltaY = this.swipe.currentY - this.swipe.startY;

    if (!this.swipe.axis && Math.hypot(deltaX, deltaY) >= 8) {
      this.swipe.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';

      if (this.swipe.axis === 'x') {
        this.track?.setPointerCapture?.(event.pointerId);
        this.track?.classList.add('is-dragging');
      }
    }

    if (this.swipe.axis === 'x') event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.swipe || event.pointerId !== this.swipe.pointerId) return;

    const deltaX = event.clientX - this.swipe.startX;
    const isHorizontalSwipe = this.swipe.axis === 'x' && Math.abs(deltaX) >= 40;

    this.cancelSwipe();

    if (!isHorizontalSwipe || this.cards.length < 2) return;

    event.preventDefault();
    this.ignoreClickUntil = performance.now() + 400;

    const direction = deltaX < 0 ? 1 : -1;
    const nextIndex = (this.activeIndex + direction + this.cards.length) % this.cards.length;
    this.selectCard(nextIndex);
  }

  cancelSwipe() {
    if (this.swipe && this.track?.hasPointerCapture?.(this.swipe.pointerId)) {
      this.track.releasePointerCapture(this.swipe.pointerId);
    }

    this.track?.classList.remove('is-dragging');
    this.swipe = null;
  }

  clampIndex(index) {
    return Math.max(0, Math.min(index, this.cards.length - 1));
  }

  circularPosition(index) {
    const count = this.cards.length;
    if (!count) return 0;

    let position = index - this.activeIndex;
    const half = count / 2;
    if (position > half) position -= count;
    if (position < -half) position += count;
    return position;
  }

  selectCard(index, focus = false) {
    const nextIndex = this.clampIndex(index);
    if (nextIndex === this.activeIndex) return;

    this.activeIndex = nextIndex;
    if (this.range) this.range.value = String(nextIndex);
    this.render(true);
    this.preloadNearbySideImages();

    if (focus) this.cards[nextIndex]?.focus({ preventScroll: true });
  }

  selectView(view) {
    if (!['front', 'side'].includes(view)) return;
    if (view === 'side' && this.cards[this.activeIndex]?.dataset.hasSide !== 'true') return;

    this.activeView = view;
    this.renderViews();
  }

  onCardKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    let nextIndex = this.activeIndex;
    if (event.key === 'ArrowLeft') nextIndex = (this.activeIndex - 1 + this.cards.length) % this.cards.length;
    if (event.key === 'ArrowRight') nextIndex = (this.activeIndex + 1) % this.cards.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.cards.length - 1;
    this.selectCard(nextIndex, true);
  }

  render(animate) {
    if (!this.cards.length) return;

    const previousRects = new Map();
    if (animate && !this.reducedMotion) {
      this.cards.forEach((card) => {
        if (!card.hidden) previousRects.set(card, card.getBoundingClientRect());
        card.getAnimations().forEach((animation) => animation.cancel());
      });
    }

    this.cards.forEach((card, index) => {
      const position = this.circularPosition(index);
      const visible = Math.abs(position) <= 3;
      const active = position === 0;

      card.hidden = !visible;
      card.dataset.position = String(position);
      card.style.setProperty('--tooto-card-order', String(position + 4));
      card.classList.toggle('is-active', active);
      card.setAttribute('aria-current', active ? 'true' : 'false');
      card.tabIndex = active ? 0 : -1;
    });

    if (this.range) this.range.value = String(this.activeIndex);
    if (this.output) this.output.textContent = `${this.activeIndex + 1} / ${this.cards.length}`;
    this.updateSliderThumb(animate);
    this.renderViews();

    if (animate && !this.reducedMotion) {
      requestAnimationFrame(() => this.animateCards(previousRects));
    }
  }

  animateCards(previousRects) {
    this.cards.forEach((card) => {
      if (card.hidden) return;
      const nextRect = card.getBoundingClientRect();
      const previousRect = previousRects.get(card);

      if (!previousRect) {
        card.animate(
          [
            { opacity: 0, transform: 'scale(.92)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 380, easing: 'cubic-bezier(.2,.72,.2,1)' }
        );
        return;
      }

      const previousCenterX = previousRect.left + previousRect.width / 2;
      const previousCenterY = previousRect.top + previousRect.height / 2;
      const nextCenterX = nextRect.left + nextRect.width / 2;
      const nextCenterY = nextRect.top + nextRect.height / 2;
      const deltaX = previousCenterX - nextCenterX;
      const deltaY = previousCenterY - nextCenterY;
      const scaleX = previousRect.width / nextRect.width;
      const scaleY = previousRect.height / nextRect.height;

      card.animate(
        [
          {
            transform: `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`,
            transformOrigin: 'center center',
          },
          { transform: 'translate(0, 0) scale(1, 1)', transformOrigin: 'center center' },
        ],
        { duration: 520, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
    });
  }

  updateSliderThumb(animate = true) {
    if (!this.range || !this.sliderThumb) return;

    const maximum = Number(this.range.max) || 0;
    const progress = maximum > 0 ? Number(this.range.value) / maximum : 0;
    const styles = getComputedStyle(this.range);
    const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
    const travel = Math.max(0, this.range.clientWidth - paddingLeft - paddingRight);
    const thumbLeft = paddingLeft + travel * progress - this.sliderThumb.offsetWidth / 2;

    if (!animate || this.reducedMotion) this.classList.remove('is-slider-ready');
    this.style.setProperty('--tooto-color-slider-thumb-x', `${thumbLeft}px`);

    if (!animate || this.reducedMotion) {
      requestAnimationFrame(() => this.classList.add('is-slider-ready'));
    }
  }

  renderViews() {
    const activeCard = this.cards[this.activeIndex];
    const sideAvailable = activeCard?.dataset.hasSide === 'true';
    if (this.activeView === 'side' && !sideAvailable) this.activeView = 'front';
    const showSide = this.activeView === 'side' && sideAvailable;

    this.cards.forEach((card, index) => {
      card.classList.toggle('is-side-view', showSide && index === this.activeIndex);
    });

    this.viewButtons.forEach((button) => {
      const view = button.dataset.tootoColorView;
      const active = view === (showSide ? 'side' : 'front');
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (view === 'side') button.disabled = !sideAvailable;
    });

    if (showSide) this.ensureSideImage(activeCard);
  }

  ensureSideImage(card) {
    const image = card?.querySelector('[data-tooto-side-image]');
    if (!image) return;
    if (image.dataset.src) {
      image.src = image.dataset.src;
      delete image.dataset.src;
    }
    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      delete image.dataset.srcset;
    }

    const markReady = () => card.classList.add('is-side-ready');
    if (image.complete && image.naturalWidth) markReady();
    else image.addEventListener('load', markReady, { once: true });
  }

  preloadNearbySideImages() {
    const activeCard = this.cards[this.activeIndex];
    if (!activeCard) return;

    if (activeCard.dataset.hasSide === 'true') this.ensureSideImage(activeCard);

    const nearbyCards = this.cards.filter((card) => {
      const position = Number(card.dataset.position);
      return !card.hidden && Math.abs(position) === 1 && card.dataset.hasSide === 'true';
    });
    const preload = () => nearbyCards.forEach((card) => this.ensureSideImage(card));
    if ('requestIdleCallback' in window) window.requestIdleCallback(preload, { timeout: 1000 });
    else window.setTimeout(preload, 250);
  }
}

if (!customElements.get('tooto-color-collection')) {
  customElements.define('tooto-color-collection', TootoColorCollection);
}
